import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";

import { Money } from "@workspace/accounting/components";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@workspace/ui/components/alert-dialog";
import { ScrollArea } from "@workspace/ui/components/scroll-area";

import { eposApi } from "../../api";
import { useEposContext } from "../epos-context";
import { removalEmptiesOrder } from "./unsent-items";

interface UnsentItemsDialogProps {
	isOpen: boolean;
	onClose: () => void;
	orderId: string;
	onConfirmed: (wasOrderEmptied: boolean) => void;
}

export function UnsentItemsDialog({
	isOpen,
	onClose,
	orderId,
	onConfirmed,
}: UnsentItemsDialogProps) {
	const { tenant, venueId, removeUnsentItems, currentOrder } = useEposContext();
	const [isRemoving, setIsRemoving] = useState(false);

	const unsentItemsQuery = useQuery({
		...eposApi.getUnsentItemsOptions(tenant, venueId, isOpen ? orderId : null),
	});

	const handleRemove = async () => {
		setIsRemoving(true);
		try {
			// Decided from the order as it stood before the removal, as the server decides it.
			const wasOrderEmptied = removalEmptiesOrder(
				(currentOrder?.items ?? []).flatMap((i) => (i.id ? [i.id] : [])),
				unsentItemsQuery.data,
			);
			await removeUnsentItems(orderId);
			onConfirmed(wasOrderEmptied);
		} catch {
			toast.error("Failed to remove unsent items");
		} finally {
			setIsRemoving(false);
		}
	};

	const data = unsentItemsQuery.data;

	return (
		<AlertDialog
			open={isOpen}
			onOpenChange={(open) => {
				if (!open && !isRemoving) onClose();
			}}
		>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>Unsent Items on Order</AlertDialogTitle>
					<AlertDialogDescription>
						The following items have not been sent to the kitchen. Remove them
						to continue.
					</AlertDialogDescription>
				</AlertDialogHeader>

				<ScrollArea className="max-h-[50vh]">
					{unsentItemsQuery.isLoading ? (
						<div className="flex items-center justify-center py-8">
							<Loader2 className="size-5 animate-spin" />
						</div>
					) : data && data.groups.length > 0 ? (
						<div className="space-y-3">
							{data.groups.map((group) => (
								<div key={group.courseId ?? "ungrouped"}>
									<div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-2 py-1 bg-muted/30">
										{group.courseName}
									</div>
									{group.items.map((item) => (
										<div
											key={item.itemId}
											className="flex justify-between items-center px-2 py-1.5 border-b"
										>
											<span className="text-sm">
												{item.quantity} × {item.name}
											</span>
											<span className="text-sm font-medium">
												<Money {...item.price} />
											</span>
										</div>
									))}
								</div>
							))}
							<div className="flex justify-between items-center px-2 py-2 pt-4 font-semibold">
								<span>Total to remove</span>
								<Money {...data.total} />
							</div>
						</div>
					) : (
						<div className="py-8 text-center text-sm text-muted-foreground">
							No unsent items found.
						</div>
					)}
				</ScrollArea>

				<AlertDialogFooter>
					<AlertDialogCancel disabled={isRemoving}>Cancel</AlertDialogCancel>
					<AlertDialogAction
						onClick={handleRemove}
						disabled={isRemoving || !data || data.itemCount === 0}
					>
						{isRemoving && <Loader2 className="mr-2 size-4 animate-spin" />}
						Remove unsent items from the bill
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}
